import React, { ReactNode } from 'react'
import {
  Button,
  Flex,
  Loading,
  Text,
  Timeline,
  useAppTheme,
} from '@workwhile/ui'
import { getTierTransitionLabel } from './utils/getTierTransitionLabel'
import { StatCard } from '../../../StatCard'
import { TierTransitionsReferencePopover } from './TierTransitionsReferencePopover'
import { formatNumber } from 'lib/number'
import { useShiftDetailContextValue } from 'pages/Shifts/ShiftDetail/context/ShiftDetailProvider'
import { TierEligibility } from 'pages/Shifts/ShiftDetail/types'
import { getTierTransitionTimestamp } from './utils/getTierTransitionTimestamp'
import { RosterTierSubtitle } from './RosterTierSubtitle'
import { useTierStatsContextValue } from '../../context/TierStatsProvider'
import { Circle, CircleCheck, Loader, XCircle } from 'lucide-react'
import { useQueryBasedFeatureFlag } from '../../../../hooks/useQueryBasedFeatureFlag'
import { useShiftDetailWorkflowsContextValue } from '../../../../context/ShiftDetailWorkflowsProvider'

export const TierTransitions = () => {
  const {
    state: {
      listingId,
      eligibilities,
      isLoading,
      isError,
      error,
      selectedTier,
    },
    actions: { setSelectedTier },
  } = useTierStatsContextValue()
  const { tierExploration } = useQueryBasedFeatureFlag()
  const {
    state: {
      id: shiftId,
      location: {
        address: { timezone },
      },
    },
  } = useShiftDetailContextValue()

  const { switchTiersFlow } = useShiftDetailWorkflowsContextValue()

  const { colors } = useAppTheme()

  const colorMap: Record<TierEligibility['activeStatus'], string> = {
    not_started: colors.neutrals[200],
    active: colors.primaries[200],
    past: colors.neutrals[200],
    skipped: colors.errors[100],
  }

  const bulletMap: Record<TierEligibility['activeStatus'], ReactNode> = {
    not_started: (
      <Circle size={14} strokeWidth={3} color={colorMap['not_started']} />
    ),
    skipped: <XCircle size={14} strokeWidth={3} color={colorMap['skipped']} />,
    active: <Loader size={14} strokeWidth={3} color={colorMap['active']} />,
    past: <CircleCheck size={14} strokeWidth={3} color={colorMap['past']} />,
  }

  const timelineItems = eligibilities.map((eligibility, index) => {
    const isPossibleToExplore =
      eligibility.activeStatus !== 'not_started' && tierExploration
    const timeString = getTierTransitionTimestamp(eligibility, timezone)
    const bullet = bulletMap[eligibility.activeStatus]

    return (
      <Timeline.Item
        key={`tier-transition-${eligibility.tier}-${index}`}
        bullet={bullet}
        color={colorMap[eligibility.activeStatus]}
      >
        <Text color="lightText" fontSize={1}>
          {timeString}
        </Text>
        <Text fontSize={3}>{getTierTransitionLabel(eligibility.tier)}</Text>
        {eligibility.tier === 'roster' ? (
          <RosterTierSubtitle shiftId={shiftId} timezone={timezone} />
        ) : null}
        <Flex gap={3} my={2} flexWrap="wrap">
          <StatCard
            title="Total Workers"
            value={formatNumber(eligibility.total)}
            size="fit"
          />
        </Flex>
        {eligibility.canTransitionToNextTier ? (
          <Button
            variant="primary"
            kind="medium"
            mt={3}
            onClick={() =>
              switchTiersFlow.open({
                listingId,
                shiftId,
              })
            }
          >
            Move to next tier
          </Button>
        ) : null}
        {isPossibleToExplore ? (
          <Button
            variant="primary"
            kind="medium"
            disabled={selectedTier === eligibility.tier}
            onClick={() => setSelectedTier(eligibility.tier)}
            mt={3}
          >
            {selectedTier === eligibility.tier ? 'Selected' : 'Explore'}
          </Button>
        ) : null}
      </Timeline.Item>
    )
  })

  return (
    <Flex flexDirection="column" width={360} p={3}>
      <Text fontSize={2} color="lightText">
        Tier Transitions
        <TierTransitionsReferencePopover />
      </Text>
      <Text fontSize={1}>
        More desirable workers are allowed to view the listing before the
        general population.
      </Text>
      {isLoading ? (
        <Flex
          minHeight={120}
          justifyContent="center"
          alignItems="center"
          mt={4}
        >
          <Loading />
        </Flex>
      ) : isError ? (
        <Text mt={4} color={colors.errors[200]} fontSize={2}>
          {error || 'Failed to load tier transitions.'}
        </Text>
      ) : timelineItems.length > 0 ? (
        <Timeline mt={4} overflowY="auto">
          {timelineItems}
        </Timeline>
      ) : (
        <Text mt={4} color="lightText">
          No transitions to explore
        </Text>
      )}
    </Flex>
  )
}
